/**
 * Business-meaningful error for admin/service operations.
 *
 * Follows the same "code IS the message" convention as `AuthError`
 * (see service.ts) and `RbacError` (see rbac.ts): the constructor sets the
 * `Error.message` to the stable machine `code`, so internal `.toThrow(/code/)`
 * assertions keep working and the code stays greppable in stack traces.
 *
 * The HTTP layer (`errorResponse` in http.ts) turns this into the wire envelope
 * `{ error: code, message, details? }` with a meaningful status (404/409/400…),
 * and admin-web localizes by `code` (see `describeError` + `errors.codes.*`
 * i18n keys). The `message`/`details` are only a fallback for consumers without
 * a code→message table.
 */
// 503: a dependency the request needs is not wired/reachable (e.g. no quota
// policy store). Distinct from 409 because retrying later can succeed.
export type ServiceErrorStatus = 400 | 401 | 403 | 404 | 409 | 410 | 413 | 422 | 429 | 500 | 503;

export class ServiceError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: ServiceErrorStatus = 400,
    /** Dynamic context surfaced to the client, e.g. `{ roles: [...] }` for
     *  interpolation into the localized message. */
    public readonly details?: Record<string, unknown>
  ) {
    super(code);
    this.name = 'ServiceError';
  }
}
